// src/screens/Revenue.jsx
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/Dashboard.css";
import "../css/Earnings.css";

import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { saveAs } from "file-saver";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Doughnut, Line } from "react-chartjs-2";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

/* ========== ENV CONFIG ========== */
const API_TRANSPORT_REVENUE = (process.env.REACT_APP_RIDES_API_URL || "").trim();
const API_FOOD_MART_REVENUE = (process.env.REACT_APP_FOOD_MART_REVENUE_API_URL || "").trim();
const API_EVENTS_BASE       = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");

/* ---------- auth helpers ---------- */
function getBearerToken() {
  const directToken =
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("adminToken") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("authToken");

  if (directToken) return String(directToken).trim();

  try {
    const rawUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    const user = rawUser ? JSON.parse(rawUser) : null;

    return String(
      user?.token ||
        user?.adminToken ||
        user?.accessToken ||
        user?.access_token ||
        user?.authToken ||
        user?.jwt ||
        ""
    ).trim();
  } catch {
    return "";
  }
}

function getAuthHeaders(withJson = false) {
  const token = getBearerToken();

  const headers = {
    Accept: "application/json",
  };

  if (withJson) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

/* ---------- helpers (local date normalization) ---------- */
const TZ = "Asia/Thimphu";

const toLocalYMD = (ts) =>
  new Date(ts).toLocaleDateString("en-CA", { timeZone: TZ });

const toLocalDT = (ts) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(new Date(ts))
    .replace(",", ""); // "DD/MM/YYYY HH:MM:SS"

const atStartOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const MONTH_ABBR = Array.from({ length: 12 }, (_, m) =>
  new Date(2000, m, 1).toLocaleString("en", { month: "short" })
);

const norm = (s) => String(s || "").trim().toLowerCase();

// completed predicates per service
const isOrderDelivered = (s) => ["delivered", "completed"].includes(norm(s));

// numeric formatter with rounding to 3 decimal places
const fmtBTN = (v) => {
  if (v === null || v === undefined || v === "") return "BTN. 0.000";
  let num = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(num)) return "BTN. 0.000";
  const rounded = Math.round(num * 1000) / 1000;
  return `BTN. ${rounded.toFixed(3)}`;
};

// Cache for API responses
const apiCache = { Transport: null, FoodMart: null, Events: null };

export default function Revenue() {
  useIdleLogout();

  const [revenueData, setRevenueData] = useState({
    Transport: [],
    FoodMart: [],
    Events: [],
  });

  const [metrics, setMetrics] = useState({
    today: 0,
    week: 0,
    month: 0,
    year: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [service, setService] = useState("Transport"); // 'Transport' | 'FoodMart'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const serviceLabel =
    service === "FoodMart" ? "Food & Mart" :
    service === "Events"   ? "Events & Entertainment" :
    service;

  /* ---------- Transform: Transport ---------- */
  const transformTransportData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      if (item?.driver_details && item?.ride_details && item?.fare_details) {
        const dd = item.driver_details || {};
        const rd = item.ride_details || {};
        const fd = item.fare_details || {};

        const completedAt = rd.completed_at || null;
        const isCompleted = Boolean(completedAt);

        const platformFee = Number(fd.platform_fee_nu ?? 0);
        const tax = Number(fd.tax_nu ?? 0);
        const totalPlatformRevenue = platformFee + tax;

        return {
          dateYMD: completedAt ? toLocalYMD(completedAt) : null,
          dateTime: completedAt ? toLocalDT(completedAt) : "—",
          name: (dd.driver_name || "").trim() || "Unknown Driver",
          platformFee,
          tax,
          revenue: totalPlatformRevenue,
          rides: 1,
          isCompleted,
          rideId: rd.ride_id ?? null,
          raw: item,
        };
      }

      return {
        dateYMD: null,
        dateTime: "—",
        name: item?.driver_name || "Unknown Driver",
        platformFee: 0,
        tax: 0,
        revenue: 0,
        rides: 0,
        isCompleted: false,
        rideId: null,
        raw: item || null,
      };
    });
  }, []);

  /* ---------- Transform: Food & Mart ---------- */
  const transformFoodMartData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      const details = item.details || {};
      const order = details.order || {};
      const customer = details.customer || {};
      const business = details.business || {};
      const items = details.items || {};
      const amounts = details.amounts || {};
      const payment = details.payment || {};

      const placedRaw = order.placed_at || null;
      const placedIso = placedRaw ? String(placedRaw).replace(" ", "T") : null;

      const platformFee = Number(amounts.platform_fee ?? item.platform_fee ?? 0);
      const tax = Number(amounts.tax ?? item.tax ?? 0);
      const revenue = Number(
        amounts.revenue_earned ?? item.revenue_earned ?? 0
      );

      const isCompleted = isOrderDelivered(order.status);

      return {
        dateYMD: placedIso ? toLocalYMD(placedIso) : null,
        dateTime: placedIso ? toLocalDT(placedIso) : "—",
        name: business.name || "Unknown Vendor",
        platformFee,
        tax,
        revenue,
        orders: Number(items.total_quantity ?? 0),
        isCompleted,
        orderId: item.order_id ?? order.id ?? null,
        ownerType: item.owner_type || order.owner_type || business.owner_type || null,
        raw: {
          ...item,
          details: {
            order,
            customer,
            business,
            items,
            amounts: {
              total_amount: amounts.total_amount ?? item.total_amount ?? 0,
              platform_fee: platformFee,
              revenue_earned: revenue,
              tax,
            },
            payment,
          },
        },
      };
    });
  }, []);

  /* ---------- Transform: Events ---------- */
  const transformEventsData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => ({
      dateYMD:   item.start_at ? toLocalYMD(item.start_at) : null,
      dateTime:  item.start_at ? toLocalDT(item.start_at) : "—",
      name:      item.title || "Unknown Event",
      category:  item.category || "—",
      platformFee: 0,
      tax:         0,
      revenue:     Number(item.gross_revenue  || 0),
      tickets:     Number(item.tickets_sold   || 0),
      bookings:    Number(item.booking_count  || 0),
      isCompleted: Number(item.gross_revenue  || 0) > 0 || Number(item.booking_count || 0) > 0,
      eventId:     item.id ?? null,
      raw:         item,
    }));
  }, []);

  /* ---------- Fetch ---------- */
  const fetchServiceData = useCallback(
    async (serviceType, controller) => {
      if (apiCache[serviceType]) return apiCache[serviceType];

      // Events uses a different base + endpoint
      if (serviceType === "Events") {
        if (!API_EVENTS_BASE) { console.warn("Missing REACT_APP_EVENTS_API_URL"); return []; }
        try {
          const url = new URL(`${API_EVENTS_BASE}/events`);
          url.searchParams.set("limit", "100");
          url.searchParams.set("page", "1");
          const res = await fetch(url.toString(), { headers: getAuthHeaders(), signal: controller.signal, cache: "no-store" });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
          const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
          const t = transformEventsData(arr);
          apiCache["Events"] = t;
          return t;
        } catch (err) {
          if (err?.name !== "AbortError") throw err;
          return [];
        }
      }

      let apiUrl = "";
      switch (serviceType) {
        case "Transport":
          apiUrl = API_TRANSPORT_REVENUE;
          break;
        case "FoodMart":
          apiUrl = API_FOOD_MART_REVENUE;
          break;
        default:
          return [];
      }

      if (!apiUrl) {
        console.warn(`Missing API URL for service: ${serviceType}`);
        return [];
      }

      try {
        const headers = getAuthHeaders(false);

        if (serviceType === "FoodMart" && !headers.Authorization) {
          throw new Error("Missing access token. Please login again.");
        }

        const response = await fetch(apiUrl, {
          method: "GET",
          headers,
          signal: controller.signal,
          cache: "no-store",
        });

        if (!response.ok) {
          let msg = `API failed: ${response.status} ${response.statusText}`;
          try {
            const json = await response.json();
            msg = json?.message || json?.error || msg;
          } catch {
            try {
              const text = await response.text();
              if (text) msg = text.slice(0, 180);
            } catch {}
          }

          if (response.status === 401) {
            throw new Error(`Unauthorized (401): Please login again. ${msg}`);
          }

          if (response.status === 403) {
            throw new Error(`Forbidden (403): You do not have permission for this revenue API. ${msg}`);
          }

          throw new Error(msg);
        }

        const json = await response.json();
        let arr = [];
        if (Array.isArray(json?.data)) arr = json.data;
        else if (Array.isArray(json?.items)) arr = json.items;
        else if (Array.isArray(json)) arr = json;

        let t = [];
        if (serviceType === "Transport") t = transformTransportData(arr);
        if (serviceType === "FoodMart") t = transformFoodMartData(arr);

        apiCache[serviceType] = t;
        return t;
      } catch (err) {
        if (err?.name !== "AbortError") {
          const raw = String(err?.message || err);

          if (/failed to fetch/i.test(raw) || err?.name === "TypeError") {
            console.error(`Error fetching ${serviceType} data:`, err);
            throw new Error(
              `Failed to fetch ${serviceType} data. Please check API URL, internet connection, and backend CORS/Authorization settings.`
            );
          }

          console.error(`Error fetching ${serviceType} data:`, err);
          throw err;
        }
        return [];
      }
    },
    [transformTransportData, transformFoodMartData, transformEventsData]
  );

  /* ---------- Load on service change ---------- */
  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const serviceData = await fetchServiceData(service, controller);
        setRevenueData((prev) => ({ ...prev, [service]: serviceData }));

        const completedRows = serviceData.filter((r) => r.isCompleted);

        const now = new Date();
        const todayStart = atStartOfDay(now);
        const startOfWeek = atStartOfDay(
          new Date(
            todayStart.getFullYear(),
            todayStart.getMonth(),
            todayStart.getDate() - todayStart.getDay()
          )
        );
        const startOfMonth = atStartOfDay(
          new Date(todayStart.getFullYear(), todayStart.getMonth(), 1)
        );
        const startOfYear = atStartOfDay(new Date(todayStart.getFullYear(), 0, 1));
        const todayStr = toLocalYMD(now);

        const todayRevenue = completedRows
          .filter((d) => d.dateYMD === todayStr)
          .reduce((s, d) => s + (d.revenue ?? 0), 0);

        const weekRevenue = completedRows
          .filter((d) => d.dateYMD && new Date(d.dateYMD) >= startOfWeek)
          .reduce((s, d) => s + (d.revenue ?? 0), 0);

        const monthRevenue = completedRows
          .filter((d) => d.dateYMD && new Date(d.dateYMD) >= startOfMonth)
          .reduce((s, d) => s + (d.revenue ?? 0), 0);

        const yearRevenue = completedRows
          .filter((d) => d.dateYMD && new Date(d.dateYMD) >= startOfYear)
          .reduce((s, d) => s + (d.revenue ?? 0), 0);

        setMetrics({
          today: todayRevenue,
          week: weekRevenue,
          month: monthRevenue,
          year: yearRevenue,
        });
      } catch (err) {
        if (err?.name !== "AbortError") {
          setError(err?.message || `Failed to load ${serviceLabel} data.`);
        }
        setMetrics({ today: 0, week: 0, month: 0, year: 0 });
      } finally {
        setLoading(false);
      }
    };

    loadData();
    return () => controller.abort();
  }, [service, fetchServiceData, serviceLabel]);

  const handleServiceChange = (newService) => {
    setService(newService);
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
  };

  const rawDataAll = revenueData[service] || [];
  const rawData = rawDataAll.filter((r) => r.isCompleted);

  const filteredData = rawData.filter((d) => {
    if (!String(d.name || "").toLowerCase().includes(searchQuery.toLowerCase()))
      return false;
    if (startDate && (d.dateYMD ?? "") < startDate) return false;
    if (endDate && (d.dateYMD ?? "") > endDate) return false;
    return true;
  });

  const totals = useMemo(() => {
    const sum = (k) =>
      filteredData.reduce((acc, r) => acc + Number(r?.[k] ?? 0), 0);
    return {
      platformFee: sum("platformFee"),
      tax: sum("tax"),
      revenue: sum("revenue"),
    };
  }, [filteredData]);

  /* ---------- MONTHLY series ---------- */
  const currentYear = new Date().getFullYear();
  const useYear = (() => {
    if (startDate && endDate) {
      const y1 = new Date(startDate).getFullYear();
      const y2 = new Date(endDate).getFullYear();
      if (y1 === y2) return y1;
    }
    return currentYear;
  })();

  function buildMonthlySeries(rows, year) {
    const sums = Array(12).fill(0);
    for (const r of rows) {
      const d = r.dateYMD ? new Date(r.dateYMD) : null;
      if (!d || d.getFullYear() !== year) continue;
      sums[d.getMonth()] += Number(r.revenue ?? 0);
    }
    return { labels: MONTH_ABBR, values: sums };
  }

  const monthly = buildMonthlySeries(filteredData, useYear);

  const lineData = {
    labels: monthly.labels,
    datasets: [
      {
        label: `Revenue per Month (BTN.) — ${useYear}`,
        data: monthly.values,
        fill: true,
        backgroundColor: "rgba(13,110,253,0.10)",
        borderColor: "#0d6efd",
        pointBackgroundColor: "#0d6efd",
        tension: 0.3,
      },
    ],
  };

  /* ---------- Doughnut ---------- */
  const sourceMap = {};
  filteredData.forEach((d) => {
    const key = d.name || (service === "Transport" ? "Transport" : "Unknown");
    sourceMap[key] = (sourceMap[key] || 0) + Number(d.revenue ?? 0);
  });

  const doughnutData = {
    labels: Object.keys(sourceMap),
    datasets: [
      {
        data: Object.values(sourceMap),
        backgroundColor: [
          "#0d6efd",
          "#198754",
          "#ffc107",
          "#dc3545",
          "#6c757d",
          "#20c997",
          "#6610f2",
          "#fd7e14",
          "#0dcaf0",
          "#adb5bd",
        ],
      },
    ],
  };

  /* ---------- Detail modals ---------- */
  const showRideDetail = (row) => {
    const r = row?.raw || {};
    const dd = r.driver_details || {};
    const pd = r.passenger_details || {};
    const rd = r.ride_details || {};
    const fd = r.fare_details || {};

    const html = `
      <div style="text-align:left; max-height:70vh; overflow:auto;">
        <div style="margin-bottom:10px;">
          <span style="display:inline-block; padding:.2rem .6rem; background:#e9f5ff; color:#0d6efd; border-radius:999px; font-weight:600;">
            Ride #${rd.ride_id ?? "—"}
          </span>
        </div>

        <h4 style="margin:10px 0 6px;">Driver</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Name</td><td>${(dd.driver_name || "—").trim()}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Phone</td><td>${dd.driver_phone || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">ID</td><td>${dd.driver_id ?? "—"}</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Passenger</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Name</td><td>${(pd.passenger_name || "—").trim()}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Phone</td><td>${pd.passenger_phone || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">ID</td><td>${pd.passenger_id ?? "—"}</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Ride</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Service</td><td>${rd.service_type || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Trip Type</td><td>${rd.trip_type || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Requested</td><td>${rd.requested_at ? toLocalDT(rd.requested_at) : "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Accepted</td><td>${rd.accepted_at ? toLocalDT(rd.accepted_at) : "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Arrived Pickup</td><td>${rd.arrived_pickup_at ? toLocalDT(rd.arrived_pickup_at) : "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Started</td><td>${rd.started_at ? toLocalDT(rd.started_at) : "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Completed</td><td>${rd.completed_at ? toLocalDT(rd.completed_at) : "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Pickup</td><td>${rd.pickup_place || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Dropoff</td><td>${rd.dropoff_place || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Distance</td><td>${rd.distance_m ?? "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Duration (s)</td><td>${rd.duration_s ?? "—"}</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Fare</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Currency</td><td>${fd.currency || "BTN"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Base Gross</td><td>${fmtBTN(fd.base_gross_nu ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Time Gross</td><td>${fmtBTN(fd.time_gross_nu ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Tips</td><td>${fmtBTN(fd.tips_nu ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Platform Fee</td><td>${fmtBTN(fd.platform_fee_nu ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Tax</td><td>${fmtBTN(fd.tax_nu ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Driver Take-Home</td><td>${fmtBTN(fd.driver_take_home_nu ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Payment Method</td><td>${fd.payment_method || "—"}</td></tr>
          </tbody>
        </table>
      </div>
    `;

    Swal.fire({
      title: `Transport Revenue Detail`,
      html,
      width: 860,
      showCloseButton: true,
      confirmButtonText: "Close",
      focusConfirm: false,
    });
  };

  const showOrderDetail = (row) => {
    const raw = row?.raw || {};
    const details = raw.details || {};
    const order = details.order || {};
    const customer = details.customer || {};
    const business = details.business || {};
    const items = details.items || {};
    const amounts = details.amounts || {};
    const payment = details.payment || {};

    const typeLabel =
      row.ownerType === "MART"
        ? "Mart"
        : row.ownerType === "FOOD"
        ? "Food"
        : "Food & Mart";

    const html = `
      <div style="text-align:left; max-height:70vh; overflow:auto;">
        <div style="margin-bottom:10px;">
          <span style="display:inline-block; padding:.2rem .6rem; background:#e9f5ff; color:#0d6efd; border-radius:999px; font-weight:600;">
            ${typeLabel} Order #${row.orderId ?? order.id ?? "—"}
          </span>
        </div>

        <h4 style="margin:10px 0 6px;">Vendor</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Name</td><td>${business.name || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Owner Type</td><td>${business.owner_type || row.ownerType || "—"}</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Customer</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Name</td><td>${customer.name || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Phone</td><td>${customer.phone || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">ID</td><td>${customer.id ?? "—"}</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Order</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Order ID</td><td>${row.orderId ?? order.id ?? "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Status</td><td>${order.status || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Placed At</td><td>${row.dateTime || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Items</td><td>${items.summary || "—"} (x${items.total_quantity ?? row.orders ?? "—"})</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Amounts</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Total Amount</td><td>${fmtBTN(amounts.total_amount ?? raw.total_amount ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Platform Fee</td><td>${fmtBTN(amounts.platform_fee ?? row.platformFee ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Revenue Earned</td><td>${fmtBTN(amounts.revenue_earned ?? row.revenue ?? 0)}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Tax</td><td>${fmtBTN(amounts.tax ?? 0)}</td></tr>
          </tbody>
        </table>

        <h4 style="margin:14px 0 6px;">Payment</h4>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Method</td><td>${payment.method || "—"}</td></tr>
          </tbody>
        </table>
      </div>
    `;

    Swal.fire({
      title: `${typeLabel} Revenue Detail`,
      html,
      width: 720,
      showCloseButton: true,
      confirmButtonText: "Close",
      focusConfirm: false,
    });
  };

  const showEventDetail = (row) => {
    const ev = row?.raw || {};
    const html = `
      <div style="text-align:left; max-height:70vh; overflow:auto;">
        <div style="margin-bottom:10px;">
          <span style="display:inline-block; padding:.2rem .6rem; background:#e9f5ff; color:#0d6efd; border-radius:999px; font-weight:600;">
            ${ev.category || "Event"} — ${ev.title || "—"}
          </span>
        </div>
        <table style="width:100%; border-collapse:collapse;">
          <tbody>
            <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Organizer</td><td>${ev.organizer_name || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Venue</td><td>${ev.venue_name || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">City</td><td>${ev.city || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Start Date</td><td>${row.dateTime || "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Tickets Sold</td><td>${Number(ev.tickets_sold || 0).toLocaleString()}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Total Bookings</td><td>${Number(ev.booking_count || 0).toLocaleString()}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Gross Revenue</td><td><strong>${fmtBTN(ev.gross_revenue)}</strong></td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Avg Rating</td><td>${ev.avg_rating ? Number(ev.avg_rating).toFixed(1) + " ★" : "—"}</td></tr>
            <tr><td style="padding:6px 0; color:#6b7280;">Live</td><td>${ev.is_live ? "Yes" : "No"}</td></tr>
          </tbody>
        </table>
      </div>`;
    Swal.fire({ title: "Event Revenue Detail", html, width: 640, showCloseButton: true, confirmButtonText: "Close", focusConfirm: false });
  };

  /* ---------- Export ---------- */
  const exportToExcel = () => {
    if (filteredData.length === 0) {
      alert("No data available to export");
      return;
    }

    let ws;

    if (service === "Transport") {
      const rows = filteredData.map((d) => ({
        "Date & Time": d.dateTime,
        "Platform Fee (BTN.)": Number((d.platformFee ?? 0).toFixed(3)),
        "Tax (BTN.)": Number((d.tax ?? 0).toFixed(3)),
        "Revenue Earned (BTN.)": Number((d.revenue ?? 0).toFixed(3)),
      }));
      ws = XLSX.utils.json_to_sheet(rows, { origin: "A4" });
    } else if (service === "Events") {
      const rows = filteredData.map((d) => ({
        "Start Date": d.dateTime,
        "Event Name": d.name,
        "Category": d.category,
        "Tickets Sold": d.tickets ?? 0,
        "Bookings": d.bookings ?? 0,
        "Gross Revenue (BTN.)": Number((d.revenue ?? 0).toFixed(3)),
      }));
      ws = XLSX.utils.json_to_sheet(rows, { origin: "A4" });
    } else {
      const rows = filteredData.map((d) => ({
        "Date & Time": d.dateTime,
        "Platform Fee (BTN.)": Number((d.platformFee ?? 0).toFixed(3)),
        "Revenue Earned (BTN.)": Number((d.revenue ?? 0).toFixed(3)),
      }));
      ws = XLSX.utils.json_to_sheet(rows, { origin: "A4" });
    }

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [`${serviceLabel} Revenue Report (COMPLETED only)`],
        [`Year: ${useYear}`],
        [`Exported: ${new Date().toLocaleString()}`],
      ],
      { origin: "A1" }
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${serviceLabel} Revenue`);
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `${serviceLabel.replace(/\s+/g, "_")}_Revenue.xlsx`);
  };

  return (
    <>
      {/* FILTER TOOLBAR */}
      <div className="revenue-toolbar card mb-4 p-3 d-flex flex-wrap align-items-center gap-3 gap-md-4 flex-md-row flex-column">
        <div
          className="input-group revenue-search flex-grow-1"
          style={{ minWidth: 220, maxWidth: 400 }}
        >
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="form-select revenue-select"
          style={{ minWidth: 160, maxWidth: 220 }}
          value={service}
          onChange={(e) => handleServiceChange(e.target.value)}
        >
          <option value="Transport">Transport</option>
          <option value="FoodMart">Food &amp; Mart</option>
          <option value="Events">Events &amp; Entertainment</option>
        </select>

        <div
          className="input-group revenue-daterange"
          style={{ minWidth: 280, maxWidth: 360 }}
        >
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="input-group-text">to</span>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      <h2 className="mb-3">{serviceLabel} Revenue Overview</h2>

      {loading && (
        <div className="d-flex justify-content-center align-items-center py-4">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "1.5rem", height: "1.5rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading {serviceLabel} data...</span>
        </div>
      )}

      {error && (
        <div className="alert alert-warning mb-4" role="alert">
          <h6 className="alert-heading">Note: {error}</h6>
          <p className="mb-0">Showing cached data or no data available.</p>
        </div>
      )}

      {/* TOTALS — Transport & FoodMart only */}
      {service !== "Events" && (
        <div className="row row-cols-1 row-cols-md-3 g-3 mb-3">
          {[
            { label: "Platform Fee (Total)", value: totals.platformFee },
            { label: "Tax (Total)", value: totals.tax },
            { label: "Revenue Earned (Total)", value: totals.revenue },
          ].map((m) => (
            <div className="col" key={m.label}>
              <div className="card h-100 revenue-metric-card">
                <div className="card-body text-center">
                  <h6 className="text-secondary fw-medium metric-label">
                    {m.label}
                  </h6>
                  <p className="fw-semibold mb-0 metric-value">
                    {fmtBTN(m.value)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* METRICS — Events only, shown at top */}
      {service === "Events" && (
        <>
          <h5 className="mb-3">{serviceLabel} Revenue Metrics (Completed)</h5>
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4 revenue-metrics-row">
            {[
              { label: "TODAY", value: metrics.today },
              { label: "THIS WEEK", value: metrics.week },
              { label: "THIS MONTH", value: metrics.month },
              { label: "THIS YEAR", value: metrics.year },
            ].map((m, i) => (
              <div className="col" key={i}>
                <div className="card h-100 revenue-metric-card">
                  <div className="card-body text-center">
                    <h6 className="text-secondary fw-medium metric-label">
                      {m.label}
                    </h6>
                    <p className="fw-semibold mb-0 metric-value">{fmtBTN(m.value)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* TABLE */}
      <div className="card earnings-table-card mb-4">
        <div className="card-body p-0">
          <h5 className="card-title px-3 pt-3">
            Completed {serviceLabel} Records
          </h5>
          <div className="table-responsive">
            <table className="earnings-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Date &amp; Time</th>
                  <th>{service === "Events" ? "Event Name" : "Source of Income"}</th>
                  {service === "Events" ? (
                    <>
                      <th>Category</th>
                      <th>Tickets Sold</th>
                      <th>Bookings</th>
                    </>
                  ) : (
                    <>
                      <th>Platform Fee</th>
                      <th>Tax</th>
                    </>
                  )}
                  <th>Revenue Earned</th>
                  <th style={{ width: 70, textAlign: "center" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.slice(0, 10).map((d, i) =>
                    service === "Transport" ? (
                      <tr key={`${d.rideId ?? "ride"}-${i}`}>
                        <td>{d.dateTime}</td>
                        <td>{d.name || "—"}</td>
                        <td>{fmtBTN(d.platformFee)}</td>
                        <td>{fmtBTN(d.tax)}</td>
                        <td>{fmtBTN(d.revenue)}</td>
                        <td style={{ textAlign: "center" }}>
                          <button type="button" className="btn btn-light btn-sm"
                            title={`View details for Ride #${d.rideId ?? ""}`}
                            onClick={() => showRideDetail(d)}>
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ) : service === "Events" ? (
                      <tr key={d.eventId ?? i}>
                        <td>{d.dateTime}</td>
                        <td>{d.name || "—"}</td>
                        <td>
                          <span className={`badge bg-${
                            d.category === "cinema" ? "primary" :
                            d.category === "concert" ? "success" :
                            d.category === "festival" ? "danger" :
                            d.category === "workshop" ? "warning" : "info"}`}>
                            {d.category}
                          </span>
                        </td>
                        <td>{d.tickets ?? 0}</td>
                        <td>{d.bookings ?? 0}</td>
                        <td>{fmtBTN(d.revenue)}</td>
                        <td style={{ textAlign: "center" }}>
                          <button type="button" className="btn btn-light btn-sm"
                            title={`View details for ${d.name}`}
                            onClick={() => showEventDetail(d)}>
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={d.orderId ?? i}>
                        <td>{d.dateTime}</td>
                        <td>{d.name || "—"}</td>
                        <td>{fmtBTN(d.platformFee)}</td>
                        <td>{fmtBTN(d.tax)}</td>
                        <td>{fmtBTN(d.revenue)}</td>
                        <td style={{ textAlign: "center" }}>
                          <button type="button" className="btn btn-light btn-sm"
                            title={`View details for Order #${d.orderId ?? ""}`}
                            onClick={() => showOrderDetail(d)}>
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {loading ? "Loading data..." : "No data for the selected criteria"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* METRICS — Transport & FoodMart only, shown at bottom */}
      {service !== "Events" && (
        <>
          <h5 className="mb-3">{serviceLabel} Revenue Metrics (Completed)</h5>
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4 revenue-metrics-row">
            {[
              { label: "TODAY", value: metrics.today },
              { label: "THIS WEEK", value: metrics.week },
              { label: "THIS MONTH", value: metrics.month },
              { label: "THIS YEAR", value: metrics.year },
            ].map((m, i) => (
              <div className="col" key={i}>
                <div className="card h-100 revenue-metric-card">
                  <div className="card-body text-center">
                    <h6 className="text-secondary fw-medium metric-label">
                      {m.label}
                    </h6>
                    <p className="fw-semibold mb-0 metric-value">{fmtBTN(m.value)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* CHARTS */}
      <h5 className="mb-3">{serviceLabel} Revenue</h5>
      <div className="row gx-4 gy-4 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100 p-3">
            <h6>Revenue per Month ({useYear})</h6>
            <Line
              data={lineData}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: { x: { ticks: { autoSkip: false } } },
              }}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="card chart-card h-100 p-3">
            <h6>Revenue by Source (Completed)</h6>
            <Doughnut
              data={doughnutData}
              options={{
                responsive: true,
                plugins: { legend: { position: "bottom" } },
              }}
            />
          </div>
        </div>
      </div>

      {/* BOTTOM BUTTONS */}
      <div className="bottom-actions d-flex justify-content-center mb-4">
        <button
          className="btn btn-outline-success me-3"
          onClick={exportToExcel}
          disabled={filteredData.length === 0}
        >
          Export Excel
        </button>
      </div>

      {/* FOOTER */}
      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All
          rights reserved
        </small>
      </footer>
    </>
  );
}
